import { type Href, useRouter } from 'expo-router';
import { type ComponentProps, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import Avatar, {
  Icon as AvatarIcon,
  Image as AvatarImage,
} from '@/components/Custom/Avatar';
import CountStepper from '@/components/FormFields/CountStepper';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
  WelcomeSecondaryAction,
} from '@/components/Welcome/WelcomeUI';
import {
  createWelcomeScheduleSlots,
  createWelcomeTeamRow,
  createWelcomeTeamRows,
  getWelcomeScheduleSlotCount,
  type WelcomeFormValues,
} from '@/layouts/Welcome/form';
import { WELCOME_COMPLETION_ROUTE } from '@/layouts/Welcome/navigation';
import { MAXIMUM_TEAM_TITLE_LENGTH } from '@/utils/constants';
import getOffsetColor from '@/utils/getOffsetColor';
import { selectionAsync } from '@/utils/Haptics';
import palette from '@/utils/palette';
import pickAvatar from '@/utils/pickAvatar';
import { getRandomTeamNameSuggestions } from '@/utils/teamNameSuggestions';

type WelcomeOrgTeamsProps = {
  completionRoute?: Href;
  scheduleRoute?: Href;
};

type WelcomeTeamRowIconButtonProps = {
  accessibilityLabel: string;
  icon: ComponentProps<typeof Icon>['name'];
  iconVariant?: ComponentProps<typeof Icon>['variant'];
  onPress: () => void;
  testID: string;
};

const MAX_TEAM_ROWS = 64;

function WelcomeTeamRowIconButton({
  accessibilityLabel,
  icon,
  iconVariant = 'typographySecondary',
  onPress,
  testID,
}: WelcomeTeamRowIconButtonProps) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      onPress={onPress}
      styleOverride={({ pressed }) => styles.rowIconButton(pressed)}
      testID={testID}
    >
      <Icon name={icon} size="xl" variant={iconVariant} />
    </Pressable>
  );
}

export default function WelcomeOrgTeams({
  completionRoute = WELCOME_COMPLETION_ROUTE,
  scheduleRoute = '/welcome/org/schedule/slots',
}: WelcomeOrgTeamsProps) {
  const { navigate } = useRouter();
  const { control, setValue } = useFormContext<WelcomeFormValues>();
  const rows = useWatch({ control, name: 'orgTeamRows' });
  const scheduleSlots = useWatch({ control, name: 'scheduleSlots' });
  const seasonStartDate = useWatch({ control, name: 'seasonStartDate' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeColorRowId, setActiveColorRowId] = useState<string | null>(null);
  const validTeamCount = rows.filter(
    (row) => row.title.trim().length > 0,
  ).length;
  const isValid = validTeamCount >= 2;

  const setRows = (nextRows: WelcomeFormValues['orgTeamRows']) => {
    setValue('orgTeamRows', nextRows, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('generatedScheduleGames', [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onChangeRowTitle = (rowId: string, title: string) => {
    setRows(rows.map((row) => (row.id === rowId ? { ...row, title } : row)));
  };

  const onChangeRowColor = (rowId: string, color: string) => {
    setRows(rows.map((row) => (row.id === rowId ? { ...row, color } : row)));
    setActiveColorRowId(null);
  };

  const onChangeRowLocalAvatarUrl = (rowId: string, localAvatarUrl: string) => {
    setRows(
      rows.map((row) => (row.id === rowId ? { ...row, localAvatarUrl } : row)),
    );
    setActiveColorRowId(null);
  };

  const onPressUploadRowAvatar = async (rowId: string) => {
    const image = await pickAvatar();

    if (image?.uri) {
      onChangeRowLocalAvatarUrl(rowId, image.uri);
    }
  };

  const onPressIncreaseTeamCount = () => {
    if (rows.length >= MAX_TEAM_ROWS) {
      return;
    }

    const [title = ''] = getRandomTeamNameSuggestions({
      count: 1,
      existingNames: rows.map((row) => row.title),
    });

    setRows([
      ...rows,
      createWelcomeTeamRow(
        title,
        rows.map((row) => row.color),
      ),
    ]);
  };

  const onPressDecreaseTeamCount = () => {
    const removedRow = rows[rows.length - 1];

    if (!removedRow) {
      return;
    }

    setRows(rows.slice(0, -1));
    if (activeColorRowId === removedRow.id) {
      setActiveColorRowId(null);
    }
  };

  const onPressGenerateTeamNames = () => {
    const suggestedTeamNames = getRandomTeamNameSuggestions({
      count: rows.length,
      existingNames: rows.map((row) => row.title),
    });

    setRows(
      rows.map((row, index) => {
        const title = suggestedTeamNames[index] ?? row.title;

        return {
          ...row,
          title,
        };
      }),
    );
    setErrorMessage(null);
  };

  const onPressDeleteRow = (rowId: string) => {
    setRows(rows.filter((row) => row.id !== rowId));
    if (activeColorRowId === rowId) {
      setActiveColorRowId(null);
    }
  };

  const onPressSkipTeams = () => {
    setRows(createWelcomeTeamRows());
    navigate(completionRoute);
  };

  const onPressContinue = () => {
    const teamTitles = rows
      .map((row) => row.title.trim())
      .filter((title) => title.length > 0);

    if (teamTitles.length < 2) {
      setErrorMessage('Add at least two teams.');
      return;
    }

    const expectedScheduleSlotCount = getWelcomeScheduleSlotCount(
      teamTitles.length,
    );

    if (scheduleSlots.length !== expectedScheduleSlotCount) {
      setValue(
        'scheduleSlots',
        createWelcomeScheduleSlots(teamTitles.length, seasonStartDate),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }

    navigate(scheduleRoute);
  };

  return (
    <WelcomeStepFrame
      title="Choose the teams playing in this first season"
      subtitle="Teams are saved to your organization and used for this season's games."
      footer={
        <Box space="sm" style={styles.footer}>
          <WelcomeCard>
            <CountStepper
              decrementAccessibilityLabel="Remove team row"
              incrementAccessibilityLabel="Add team row"
              label="Number of Teams"
              max={MAX_TEAM_ROWS}
              onDecrement={onPressDecreaseTeamCount}
              onIncrement={onPressIncreaseTeamCount}
              testID="welcome-team-count"
              value={rows.length}
            />
          </WelcomeCard>
          <WelcomeActions>
            <WelcomeSecondaryAction
              label="Generate Names"
              icon="sparkles-outline"
              onPress={onPressGenerateTeamNames}
            />
            <WelcomeSecondaryAction
              label="Skip teams"
              icon="arrow-forward-outline"
              onPress={onPressSkipTeams}
            />
            <WelcomePrimaryAction
              label="Continue"
              disabled={!isValid}
              onPress={onPressContinue}
            />
          </WelcomeActions>
        </Box>
      }
    >
      <WelcomeStepScrollContent>
        <Box space="sm">
          {rows.map((row, index) => {
            const title = row.title.trim();
            const fallbackName = 'Team';
            const displayName = title || fallbackName;
            const expanded = activeColorRowId === row.id;

            return (
              <Box key={row.id} style={styles.teamCard}>
                <Box direction="horizontal" alignItems="stretch">
                  <Box
                    direction="horizontal"
                    space="md"
                    alignItems="center"
                    style={styles.teamCardMain}
                  >
                    <Pressable
                      accessibilityLabel={`Upload photo for ${displayName}`}
                      accessibilityRole="button"
                      onPress={() => onPressUploadRowAvatar(row.id)}
                      styleOverride={({ pressed }) =>
                        styles.colorButton(pressed)
                      }
                      testID={`welcome-team-row-${index}-avatar-upload-button`}
                    >
                      <Avatar
                        color={row.color}
                        size="md"
                        testID={`welcome-team-row-${index}-avatar`}
                      >
                        <AvatarIcon
                          name="camera-outline"
                          size="xl"
                          color={getOffsetColor(row.color, false)}
                        />
                        {row.localAvatarUrl ? (
                          <AvatarImage
                            localUrl={row.localAvatarUrl}
                            size="md"
                          />
                        ) : null}
                      </Avatar>
                    </Pressable>
                    <TextInput
                      autoCapitalize="words"
                      enterKeyHint="done"
                      maxLength={MAXIMUM_TEAM_TITLE_LENGTH}
                      placeholder="Team name"
                      value={row.title}
                      onChangeText={(value) => onChangeRowTitle(row.id, value)}
                      style={styles.input}
                    />
                  </Box>
                  <Box
                    direction="horizontal"
                    space="sm"
                    style={styles.rowActions}
                  >
                    <WelcomeTeamRowIconButton
                      accessibilityLabel={`Edit color for ${displayName}`}
                      icon="color-palette-outline"
                      iconVariant="secondary"
                      onPress={() =>
                        setActiveColorRowId(expanded ? null : row.id)
                      }
                      testID={`welcome-team-row-${index}-edit-color-button`}
                    />
                    <WelcomeTeamRowIconButton
                      accessibilityLabel="Delete team"
                      icon="trash-outline"
                      onPress={() => onPressDeleteRow(row.id)}
                      testID={`welcome-team-row-${index}-delete-button`}
                    />
                  </Box>
                </Box>
                {expanded ? (
                  <Box
                    direction="horizontal"
                    flexWrap="wrap"
                    style={styles.palette}
                  >
                    {palette.map((paletteColor) => {
                      const selected = row.color === paletteColor;

                      return (
                        <Pressable
                          accessibilityLabel={`Choose ${paletteColor} for ${displayName}`}
                          accessibilityRole="button"
                          accessibilityState={{ selected }}
                          key={paletteColor}
                          onPress={() => {
                            selectionAsync();
                            onChangeRowColor(row.id, paletteColor);
                          }}
                          styleOverride={({ pressed }) =>
                            styles.swatch(paletteColor, selected, pressed)
                          }
                          testID={`welcome-team-row-${index}-color-${paletteColor}`}
                        >
                          {selected ? (
                            <Icon
                              color={getOffsetColor(paletteColor, true)}
                              name="checkmark"
                              size="sm"
                            />
                          ) : null}
                        </Pressable>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            );
          })}
          {errorMessage ? (
            <Text variant="error" center selectable>
              {errorMessage}
            </Text>
          ) : null}
        </Box>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}

const styles = StyleSheet.create((theme) => ({
  colorButton: (pressed: boolean) => ({
    borderRadius: theme.borderRadius.full,
    opacity: pressed ? 0.7 : 1,
    _web: {
      _hover: {
        opacity: 0.9,
      },
    },
  }),
  input: {
    flex: 1,
  },
  footer: {
    alignSelf: 'center',
    maxWidth: theme.size(132),
    width: '100%',
  },
  palette: {
    gap: theme.size(1),
    paddingBottom: theme.space('lg'),
    paddingHorizontal: theme.size(1),
    paddingTop: theme.space('sm'),
  },
  rowActions: {
    alignItems: 'center',
    flexShrink: 0,
    paddingRight: theme.space('md'),
    paddingVertical: theme.space('lg'),
  },
  rowIconButton: (pressed: boolean) => ({
    alignItems: 'center',
    backgroundColor: pressed ? theme.colors.surfaceSecondary : 'transparent',
    borderRadius: theme.borderRadius.xl,
    height: theme.size(10),
    justifyContent: 'center',
    width: theme.size(10),
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
  }),
  teamCard: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    overflow: 'hidden',
    width: '100%',
  },
  teamCardMain: {
    flex: 1,
    minWidth: 0,
    paddingLeft: theme.space('md'),
    paddingRight: theme.space('sm'),
    paddingVertical: theme.space('lg'),
  },
  swatch: (color: string, selected: boolean, pressed: boolean) => ({
    alignItems: 'center',
    backgroundColor: color,
    borderColor: selected ? theme.colors.typography : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.full,
    borderWidth: selected ? theme.size(0.75) : theme.size(0.5),
    height: theme.size(6),
    justifyContent: 'center',
    opacity: pressed ? 0.7 : 1,
    width: theme.size(6),
    _web: {
      _hover: {
        opacity: 0.8,
      },
    },
  }),
}));
